import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import DOMPurify from "dompurify";
import { getContentBySlug } from "../api/contentApi";
import { trackContentView } from "../api/analyticsApi";
import NewsRating from "../components/NewsRating";

const DEFAULT_IMAGE = "/images/default-content.jpg";

const getApiOrigin = () => {
  const envUrl =
    process.env.REACT_APP_API_ORIGIN ||
    process.env.REACT_APP_API_URL ||
    process.env.REACT_APP_API_BASE_URL ||
    "";

  if (envUrl && envUrl.startsWith("http")) {
    return envUrl.replace(/\/api\/?$/, "").replace(/\/$/, "");
  }

  return window.location.origin;
};

const cleanPath = (value) => String(value || "").trim().replace(/\\/g, "/");

const unique = (items) => [...new Set(items.filter(Boolean))];

const buildImageCandidates = (image) => {
  const src = cleanPath(image);
  const apiOrigin = getApiOrigin();

  if (!src) return [DEFAULT_IMAGE];

  if (src.startsWith("data:") || src.startsWith("blob:")) {
    return [src, DEFAULT_IMAGE];
  }

  if (/^https?:\/\/(localhost|127\.0\.0\.1)(:\d+)?\//i.test(src)) {
    try {
      const url = new URL(src);
      return unique([
        `${window.location.origin}${url.pathname}`,
        `${apiOrigin}${url.pathname}`,
        url.pathname,
        DEFAULT_IMAGE,
      ]);
    } catch {
      return [DEFAULT_IMAGE];
    }
  }

  if (/^https?:\/\//i.test(src)) {
    try {
      const url = new URL(src);
      return unique([
        src,
        `${apiOrigin}${url.pathname}`,
        `${window.location.origin}${url.pathname}`,
        url.pathname,
        DEFAULT_IMAGE,
      ]);
    } catch {
      return [src, DEFAULT_IMAGE];
    }
  }

  if (src.startsWith("//")) {
    return unique([`https:${src}`, src, DEFAULT_IMAGE]);
  }

  if (src.startsWith("/uploads/")) {
    return unique([
      `${apiOrigin}${src}`,
      `${window.location.origin}${src}`,
      src,
      DEFAULT_IMAGE,
    ]);
  }

  if (src.startsWith("uploads/")) {
    return unique([
      `${apiOrigin}/${src}`,
      `${window.location.origin}/${src}`,
      `/${src}`,
      DEFAULT_IMAGE,
    ]);
  }

  if (src.startsWith("/")) {
    return unique([
      `${window.location.origin}${src}`,
      `${apiOrigin}${src}`,
      src,
      DEFAULT_IMAGE,
    ]);
  }

  return unique([
    `${window.location.origin}/${src}`,
    `${apiOrigin}/${src}`,
    `/${src}`,
    DEFAULT_IMAGE,
  ]);
};

const normalizeArticleHtml = (html) => {
  return String(html || "")
    .replace(/&nbsp;/gi, " ")
    .replace(/&#160;/gi, " ")
    .replace(/\u00a0/g, " ")
    .replace(/\u202f/g, " ")
    .replace(/[ \t]{2,}/g, " ");
};

const MIN_IMAGE_WIDTH_PX = 80;
const MAX_IMAGE_WIDTH_PX = 1400;
const MIN_IMAGE_WIDTH_PERCENT = 10;
const MAX_IMAGE_WIDTH_PERCENT = 100;
const MIN_IMAGE_HEIGHT_PX = 40;
const MAX_IMAGE_HEIGHT_PX = 1200;
const MIN_IMAGE_BORDER_PX = 0;
const MAX_IMAGE_BORDER_PX = 40;
const DEFAULT_IMAGE_BORDER_COLOR = "#cbd5e1";

const clampNumber = (value, min, max) => {
  const number = Number(value);

  if (!Number.isFinite(number)) return min;

  return Math.min(Math.max(number, min), max);
};

const normalizeImageWidth = (value, fallback = "100%") => {
  const rawValue = String(value || "").trim().toLowerCase();
  const rawFallback = String(fallback || "100%").trim().toLowerCase();
  const source = rawValue || rawFallback;
  const match = source.match(/^(\d+(?:\.\d+)?)(px|%)?$/);

  if (!match) return "100%";

  const amount = Number(match[1]);
  const unit = match[2] || "%";

  if (unit === "%") {
    return `${clampNumber(amount, MIN_IMAGE_WIDTH_PERCENT, MAX_IMAGE_WIDTH_PERCENT)}%`;
  }

  return `${Math.round(clampNumber(amount, MIN_IMAGE_WIDTH_PX, MAX_IMAGE_WIDTH_PX))}px`;
};

const normalizeImageHeight = (value, fallback = "auto") => {
  const rawValue = String(value ?? "").trim().toLowerCase();
  const rawFallback = String(fallback ?? "auto").trim().toLowerCase();
  const source = rawValue || rawFallback;

  if (!source || source === "auto") return "auto";

  const match = source.match(/^(\d+(?:\.\d+)?)(px)?$/);

  if (!match) return "auto";

  return `${Math.round(clampNumber(Number(match[1]), MIN_IMAGE_HEIGHT_PX, MAX_IMAGE_HEIGHT_PX))}px`;
};

const normalizeImageBorderWidth = (value, fallback = "0") => {
  const rawValue = String(value ?? "").trim().toLowerCase();
  const rawFallback = String(fallback ?? "0").trim().toLowerCase();
  const source = rawValue || rawFallback;
  const match = source.match(/^(\d+(?:\.\d+)?)(px)?$/);

  if (!match) return 0;

  return Math.round(clampNumber(Number(match[1]), MIN_IMAGE_BORDER_PX, MAX_IMAGE_BORDER_PX));
};

const buildSafeImageStyle = ({ width, height = "auto", borderWidth = 0 } = {}) => {
  const normalizedWidth = normalizeImageWidth(width, "100%");
  const normalizedHeight = normalizeImageHeight(height, "auto");
  const normalizedBorderWidth = normalizeImageBorderWidth(borderWidth, "0");
  const heightRule =
    normalizedHeight === "auto"
      ? "height: auto !important;"
      : `height: ${normalizedHeight} !important;`;
  const borderRule =
    normalizedBorderWidth > 0
      ? `border: ${normalizedBorderWidth}px solid ${DEFAULT_IMAGE_BORDER_COLOR} !important;`
      : "border: 0 !important;";

  return [
    `width: ${normalizedWidth} !important;`,
    "max-width: 100% !important;",
    heightRule,
    "object-fit: contain !important;",
    "box-sizing: border-box !important;",
    borderRule,
  ].join(" ");
};

const getImageWidthFromElement = (img) => {
  if (!img) return "100%";

  const dataWidth = img.getAttribute("data-img-width");

  if (dataWidth) return normalizeImageWidth(dataWidth, "100%");

  const styleWidth = img.style?.width;

  if (styleWidth) return normalizeImageWidth(styleWidth, "100%");

  const attrWidth = img.getAttribute("width");

  if (attrWidth) return normalizeImageWidth(`${attrWidth}px`, "100%");

  return "100%";
};

const getImageHeightFromElement = (img) => {
  if (!img) return "auto";

  const dataHeight = img.getAttribute("data-img-height");

  if (dataHeight) return normalizeImageHeight(dataHeight, "auto");

  const styleHeight = img.style?.height;

  if (styleHeight) return normalizeImageHeight(styleHeight, "auto");

  const attrHeight = img.getAttribute("height");

  if (attrHeight) return normalizeImageHeight(`${attrHeight}px`, "auto");

  return "auto";
};

const getImageBorderWidthFromElement = (img) => {
  if (!img) return 0;

  return normalizeImageBorderWidth(
    img.getAttribute("data-img-border") || img.style?.borderWidth || "0"
  );
};

const normalizeArticleImageMarkup = (html) => {
  if (typeof window === "undefined" || typeof window.DOMParser === "undefined") {
    return html;
  }

  const parser = new window.DOMParser();
  const doc = parser.parseFromString(`<div>${html}</div>`, "text/html");
  const wrapper = doc.body.firstElementChild;

  if (!wrapper) return html;

  wrapper.querySelectorAll(".article-image-grid").forEach((grid) => {
    const fragment = doc.createDocumentFragment();

    grid.querySelectorAll("img").forEach((img, index) => {
      if (index > 0) {
        fragment.appendChild(doc.createTextNode(" "));
      }

      fragment.appendChild(img.cloneNode(true));
    });

    grid.replaceWith(fragment);
  });

  wrapper.querySelectorAll("*").forEach((node) => {
    if (node.tagName === "IMG") {
      const width = getImageWidthFromElement(node);
      const height = getImageHeightFromElement(node);
      const borderWidth = getImageBorderWidthFromElement(node);

      node.classList.add("article-editor-image", "article-img-custom");
      node.setAttribute("data-img-width", width);
      node.setAttribute("data-img-height", height);
      node.setAttribute("data-img-border", String(borderWidth));
      node.setAttribute("style", buildSafeImageStyle({ width, height, borderWidth }));

      if (width.endsWith("px")) {
        node.setAttribute("width", width.replace("px", ""));
      } else {
        node.removeAttribute("width");
      }

      if (height.endsWith("px")) {
        node.setAttribute("height", height.replace("px", ""));
      } else {
        node.removeAttribute("height");
      }

      return;
    }

    node.removeAttribute("style");
    node.removeAttribute("data-img-width");
    node.removeAttribute("data-img-height");
    node.removeAttribute("data-img-border");
    node.removeAttribute("width");
    node.removeAttribute("height");
  });

  return wrapper.innerHTML;
};

const unwrapContentResponse = (result) => {
  if (!result) return null;

  // اگر خود آبجکت خبر برگشته باشد، همان را برگردان.
  // نکته مهم: آبجکت خبر خودش فیلدی به نام content دارد، پس نباید صرفاً با دیدن result.content
  // تصور کنیم پاسخ API یک envelope مثل { content: {...} } است.
  if (result._id || result.slug || result.title) {
    return result;
  }

  // اگر پاسخ API به شکل { success: true, content: {...} } باشد.
  if (result.content && typeof result.content === "object") {
    return result.content;
  }

  // اگر پاسخ API به شکل { data: { content: {...} } } باشد.
  if (result.data?.content && typeof result.data.content === "object") {
    return result.data.content;
  }

  return result;
};

const safeDecodeSlug = (value) => {
  try {
    return decodeURIComponent(value || "");
  } catch {
    return value || "";
  }
};

export default function ContentDetail({ type, content: initialContent }) {
  const { slug } = useParams();

  const [item, setItem] = useState(initialContent || null);
  const [loading, setLoading] = useState(!initialContent);
  const [error, setError] = useState("");
  const [rating, setRating] = useState(initialContent?.userRate || 0);
  const [imageIndex, setImageIndex] = useState(0);
  const [imageFailed, setImageFailed] = useState(false);
  const trackedContentRef = useRef("");

  useEffect(() => {
    if (initialContent) {
      setItem(initialContent);
      setRating(initialContent.userRate || 0);
      setLoading(false);
      return;
    }

    const fetchContent = async () => {
      try {
        setLoading(true);
        setError("");

        const result = await getContentBySlug(safeDecodeSlug(slug));
        const content = unwrapContentResponse(result);

        if (!content || !(content._id || content.slug || content.title)) {
          setError("محتوا پیدا نشد");
          setItem(null);
          return;
        }

        if (type && content?.type && content.type !== type) {
          setError("محتوای موردنظر با این مسیر مطابقت ندارد.");
          setItem(null);
          return;
        }

        setItem(content);
        setRating(content?.userRate || 0);
        setImageIndex(0);
        setImageFailed(false);
      } catch (err) {
        setError(err.message || "خطا در دریافت محتوا");
        setItem(null);
      } finally {
        setLoading(false);
      }
    };

    fetchContent();
  }, [slug, type, initialContent]);

  useEffect(() => {
    const contentKey = item?._id || item?.slug || "";

    if (!contentKey || trackedContentRef.current === contentKey) {
      return;
    }

    trackedContentRef.current = contentKey;

    trackContentView({
      contentId: item?._id,
      slug: item?.slug || safeDecodeSlug(slug),
      path: window.location.pathname,
      referrer: document.referrer || "",
    }).then((result) => {
      if (typeof result?.viewsCount === "number") {
        setItem((current) =>
          current
            ? {
                ...current,
                viewsCount: result.viewsCount,
              }
            : current
        );
      }
    });
  }, [item?._id, item?.slug, slug]);

  const sanitizedContent = useMemo(() => {
    return DOMPurify.sanitize(
      normalizeArticleImageMarkup(normalizeArticleHtml(item?.content || "")),
      {
        ADD_ATTR: ["target", "class", "data-img-width", "data-img-height", "data-img-border", "alt", "style", "width", "height"],
        ADD_TAGS: ["figure"],
      }
    );
  }, [item?.content]);

  const imageCandidates = useMemo(() => {
    return buildImageCandidates(item?.image);
  }, [item?.image]);

  const imageSrc = imageCandidates[imageIndex] || DEFAULT_IMAGE;

  const handleImageError = () => {
    setImageIndex((prev) => {
      const next = prev + 1;

      if (next < imageCandidates.length) {
        return next;
      }

      setImageFailed(true);
      return prev;
    });
  };

  const handleRating = (value) => {
    setRating(value);
    console.log("User rated:", value);
  };

  if (loading) {
    return (
      <main className="content-page">
        <section className="content-state glass-card">
          در حال دریافت جزئیات محتوا...
        </section>
      </main>
    );
  }

  if (error || !item) {
    return (
      <main className="content-page">
        <section className="page-hero glass-card">
          <h1>محتوا پیدا نشد</h1>
          <p>{error || "مطلب موردنظر وجود ندارد."}</p>
          <Link to="/" className="primary-link">
            بازگشت
          </Link>
        </section>
      </main>
    );
  }

  const isNews = item.type === "news";
  const backPath = isNews ? "/news" : "/courses";
  const backLabel = isNews ? "بازگشت به اخبار" : "بازگشت به دوره‌ها";

  return (
    <main className="content-page detail-page-light">
      <article className="detail-card glass-card detail-card-light">
        <div className="detail-content detail-content-light">
          {item.category && (
            <span className="content-card-category">{item.category}</span>
          )}

          <h1>{item.title}</h1>

          {isNews && (
            <p className="detail-meta">
              {item.publishAt
                ? new Date(item.publishAt).toLocaleString("fa-IR", {
                    year: "numeric",
                    month: "2-digit",
                    day: "2-digit",
                    hour: "2-digit",
                    minute: "2-digit",
                  })
                : "بدون زمان انتشار"}
            </p>
          )}

          {item.type === "course" && (
            <p className="detail-meta">
              سطح دوره: {item.level || "نامشخص"} | مدت زمان:{" "}
              {item.duration || "نامشخص"}
            </p>
          )}

          <p className="detail-meta">
            بازدید: {new Intl.NumberFormat("fa-IR").format(Number(item.viewsCount || 0))}
          </p>

          {!imageFailed && (
            <div className="detail-inline-image">
              <img
                src={imageSrc}
                alt={item.title || "تصویر محتوا"}
                onError={handleImageError}
                loading="eager"
              />
            </div>
          )}

          {imageFailed && (
            <div className="detail-image-missing">
              تصویر این محتوا در مسیر ذخیره‌شده قابل دسترسی نیست.
            </div>
          )}

          {item.summary && <p className="detail-summary">{item.summary}</p>}

          <div
            className="article-content"
            dangerouslySetInnerHTML={{ __html: sanitizedContent }}
          />

          {isNews && (
            <div className="detail-rating-box">
              <span>امتیاز شما به این خبر:</span>
              <NewsRating initialRate={rating} onRate={handleRating} />
            </div>
          )}

          <Link to={backPath} className="primary-link detail-back-link">
            {backLabel}
          </Link>
        </div>
      </article>
    </main>
  );
}
